import { ref, computed } from "vue";
import { defineStore } from "pinia";

export interface Review {
  id: string | number;
  score: number;
  customer?: string;
  maker_slug?: string;
  comment?: string;
  [key: string]: unknown;
}

export const useReviewsStore = defineStore("reviews", () => {
  // --------------- State ---------------
  const reviews = ref<Review[] | false>(false);
  const reviewMessage = ref<string | false>(false);
  const reviewsLoading = ref(false);

  // --------------- Getters ---------------
  const topReviews = computed(() => {
    if (!reviews.value) return [];
    const top = reviews.value.filter((r) => r.score >= 4 && r.customer);
    return Array.from(
      top
        .reduce((unique, review) => {
          if (!unique.has(review.customer!)) {
            unique.set(review.customer!, review);
          }
          return unique;
        }, new Map<string, Review>())
        .values(),
    );
  });

  // --------------- Actions ---------------
  async function getReviews() {
    const config = useRuntimeConfig();
    reviewsLoading.value = true;

    try {
      const response = await $fetch<{ data: Review[] }>(
        `${config.public.apiBase}/api/public/reviews?limit=9999`,
      );
      reviews.value = response.data;
      reviewsLoading.value = false;
    } catch {
      reviews.value = false;
      reviewsLoading.value = false;
    }
  }

  async function createReview(payload: { id: string; body: Record<string, unknown> }) {
    const config = useRuntimeConfig();

    try {
      await $fetch(`${config.public.apiBase}/api/public/reviews/${payload.id}`, {
        method: "POST",
        body: payload.body,
      });
      reviewMessage.value = "Thanks for sharing";
    } catch {
      // Error
    }
  }

  return {
    reviews,
    reviewMessage,
    reviewsLoading,
    topReviews,
    getReviews,
    createReview,
  };
});
